/**
 * Alert panel rendering and interaction
 * Desktop: vertical scrollable sidebar list
 * Mobile: single-card carousel with swipe navigation
 */

// Zwaailicht = P2000 priority A1 or P1 (lights & sirens authorised).
// Mirrors backend/services/zwaailicht.py — drives the small beacon icon next to A1/P1 titles.
const ZWAAILICHT_PRIORITIES = new Set(['A1', 'P1']);
function isZwaailicht(alert) {
    return ZWAAILICHT_PRIORITIES.has((alert.priority || '').toUpperCase());
}
import {
    userLocation,
    alerts,
    currentAlertIndex,
    setCurrentAlertIndex,
    setAllDisplayedAlerts,
    allDisplayedAlerts,
    getCachedDistance,
    getCachedRouteDistance,
    filterRadiusKm,
    activeBursts,
} from './state.js';
import { escapeHtml, formatAge, getAlertDetailUrl, shareAlert, getBurstUrl, getDistanceKm } from './utils.js';
import { getAlertTrending } from './trending.js';
import {
    updateTrackingBeam,
    fitBoundsToAlert,
    highlightMarker,
    openMarkerPopup,
    setView,
} from './map.js';

// DOM references
let listEl = null;
let panelEl = null;
let viewToggleBtn = null; // Header icon: card view ⇄ list view (mobile)

// Mobile card mode state
let isListMode = false;     // Full list mode (mobile)

// List mode drag handle state (mobile)
let touchStartY = 0;
let dragMoved = false;

// Horizontal swipe carousel state (mobile)
let swipeStartX = 0;
let swipeStartY = 0;
let isSwiping = false;
let swipeLastX = 0;           // Last touch X for velocity
let swipeLastTime = 0;        // Last touch time for velocity
let swipeVelocity = 0;        // px/ms


/**
 * Initialize panel with DOM references
 */
export function initCarousel() {
    listEl = document.getElementById('alert-list');
    panelEl = document.getElementById('alert-panel');

    const panelHeader = document.getElementById('panel-header');

    if (listEl) {
        listEl.addEventListener('click', handleRowClick);

        // Swipe left/right on alert rows to navigate (mobile)
        listEl.addEventListener('touchstart', handleRowSwipeStart, { passive: true });
        listEl.addEventListener('touchmove', handleRowSwipeMove, { passive: false });
        listEl.addEventListener('touchend', handleRowSwipeEnd);
        listEl.addEventListener('touchcancel', handleRowSwipeEnd);
    }

    // List mode drag handle (mobile) — swipe down or tap to close
    if (panelHeader) {
        panelHeader.addEventListener('touchstart', handleSheetTouchStart, { passive: true });
        panelHeader.addEventListener('touchmove', handleSheetTouchMove, { passive: true });
        panelHeader.addEventListener('touchend', handleSheetTouchEnd);
        panelHeader.addEventListener('click', handleSheetHeaderClick);
    }

    // Wire up the panel-header view-mode toggle (mobile-only behavior, but
    // the button lives in the header on all viewports — CSS hides on desktop).
    viewToggleBtn = document.getElementById('panel-view-toggle');
    if (viewToggleBtn) {
        viewToggleBtn.addEventListener('click', (e) => {
            // The header itself listens for click-to-close in list mode;
            // stop the event so we don't toggle twice.
            e.stopPropagation();
            toggleListMode();
        });
    }

    const mobileQuery = window.matchMedia('(max-width: 768px)');
    mobileQuery.addEventListener('change', (e) => {
        if (!e.matches) {
            // Rotated to desktop: clean up mobile-only state
            if (isListMode) {
                isListMode = false;
                panelEl?.classList.remove('show-list');
                _syncViewToggle();
            }
            // Clear stale carousel transform for desktop layout
            if (listEl) listEl.style.transform = '';
        }
    });

    // Re-snap carousel on resize/orientation change within mobile range
    let resizeTimer;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            if (window.innerWidth <= 768 && !isListMode) {
                snapCarousel();
            }
        }, 150);
    });
}

/**
 * Toggle between single-card and full-list mode (mobile)
 */
function toggleListMode() {
    if (!panelEl) return;
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    isListMode = !isListMode;
    panelEl.classList.toggle('show-list', isListMode);
    _syncViewToggle();

    if (isListMode) {
        // Scroll active row into view
        const activeRow = listEl?.querySelector('.alert-row.active');
        if (activeRow) {
            setTimeout(() => activeRow.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
        }
    } else {
        // Re-snap carousel when returning from list mode
        requestAnimationFrame(() => snapCarousel());
    }
}

function _syncViewToggle() {
    if (!viewToggleBtn) return;
    viewToggleBtn.classList.toggle('is-list', isListMode);
    viewToggleBtn.setAttribute('aria-pressed', isListMode ? 'true' : 'false');
    viewToggleBtn.setAttribute('title', isListMode ? 'Toon kaart' : 'Toon volledige lijst');
    viewToggleBtn.setAttribute('aria-label', isListMode ? 'Toon kaart' : 'Toon volledige lijst');
}


/**
 * Handle click on an alert row → focus map on alert
 */
function handleRowClick(e) {
    const row = e.target.closest('.alert-row');
    if (!row) return;

    // Don't intercept share button or explicit link clicks
    if (e.target.closest('.row-share-btn') || e.target.closest('a')) return;

    const index = parseInt(row.dataset.index, 10);
    if (isNaN(index)) return;

    const isMobile = window.innerWidth <= 768;
    const isAlreadyActive = index === currentAlertIndex;

    if (isMobile && !isListMode) {
        // Single-card mode: tap on active card navigates to detail page
        if (isAlreadyActive) {
            const alert = allDisplayedAlerts[index];
            if (alert) {
                window.location.href = getAlertDetailUrl(alert);
            }
            return;
        }
        // Tapped a non-active card (shouldn't happen in card mode, but handle it)
        focusAlertByIndex(index);
        return;
    }

    if (isMobile && isListMode && isAlreadyActive) {
        // List mode: second tap on active row → navigate to detail page
        const alert = allDisplayedAlerts[index];
        if (alert) {
            window.location.href = getAlertDetailUrl(alert);
        }
        return;
    }

    focusAlertByIndex(index);
}


// =============================================================================
// List Mode Drag Handle (mobile)
// =============================================================================

function handleSheetTouchStart(e) {
    if (window.innerWidth > 768) return;
    if (!isListMode) return;
    touchStartY = e.touches[0].clientY;
    dragMoved = false;
}

function handleSheetTouchMove(e) {
    if (window.innerWidth > 768 || !isListMode) return;
    const deltaY = e.touches[0].clientY - touchStartY;
    if (Math.abs(deltaY) > 10) {
        dragMoved = true;
    }
}

function handleSheetTouchEnd(e) {
    if (window.innerWidth > 768 || !isListMode) return;
    if (!dragMoved) return;
    const deltaY = e.changedTouches[0].clientY - touchStartY;
    // Swipe down on drag handle → close list mode
    if (deltaY > 60) {
        toggleListMode();
    }
}

function handleSheetHeaderClick(e) {
    if (window.innerWidth > 768) return;
    if (!isListMode) return;
    if (dragMoved) return;
    // Ignore clicks on interactive children (filter pills, view-toggle, etc.)
    if (e?.target?.closest('button, a')) return;
    // Tap empty header / drag handle → close list mode
    toggleListMode();
}


// =============================================================================
// Horizontal swipe carousel (mobile) — continuous touch tracking
// =============================================================================

/**
 * Get the width of a single card slot (card width + margins)
 */
function getCardSlotWidth() {
    // Card width = 100vw - 56px, plus 12px total margin (6px each side)
    return window.innerWidth - 56 + 12;
}

// Maps an alert's navigation index → its DOM slot position among all rendered
// cards. Burst cards are woven in between alerts, so a card's on-screen slot no
// longer equals its navigation index. Rebuilt every renderCarousel().
let _alertSlotByIndex = [];

/**
 * Compute the translateX value to center the card at `index`
 */
function getCarouselOffset(index) {
    const slotWidth = getCardSlotWidth();
    // Card is (100vw - 56px) wide with 6px margin each side.
    // To center: (56 / 2) - 6 = 22px left padding.
    // Look up the alert's DOM slot — burst cards interleaved before it shift
    // its on-screen position past its navigation index.
    const slot = _alertSlotByIndex[index] ?? index;
    return -(slot * slotWidth) + 22;
}

/**
 * Apply transform to the carousel track (panel-list), optionally animated
 */
function setCarouselTransform(offset, animate) {
    if (!listEl) return;
    if (animate) {
        listEl.classList.remove('dragging');
    } else {
        listEl.classList.add('dragging');
    }
    listEl.style.transform = `translateX(${offset}px)`;
}

/**
 * Snap carousel to the current alert index (animated)
 */
function snapCarousel() {
    const offset = getCarouselOffset(currentAlertIndex);
    setCarouselTransform(offset, true);
}

function handleRowSwipeStart(e) {
    if (window.innerWidth > 768) return;
    if (isListMode) return;

    swipeStartX = e.touches[0].clientX;
    swipeStartY = e.touches[0].clientY;
    swipeLastX = swipeStartX;
    swipeLastTime = Date.now();
    swipeVelocity = 0;
    isSwiping = false;
}

function handleRowSwipeMove(e) {
    if (window.innerWidth > 768) return;
    if (isListMode) return;
    if (e.touches.length > 1) return; // Ignore multi-touch

    const dx = e.touches[0].clientX - swipeStartX;
    const dy = e.touches[0].clientY - swipeStartY;

    // Determine swipe axis on first significant movement
    if (!isSwiping && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        isSwiping = true;
    }

    if (!isSwiping) return;
    e.preventDefault();

    // Track velocity (exponential moving average over last few frames)
    const now = Date.now();
    const dt = now - swipeLastTime;
    if (dt > 0) {
        const instantVelocity = (e.touches[0].clientX - swipeLastX) / dt;
        swipeVelocity = 0.7 * instantVelocity + 0.3 * swipeVelocity;
    }
    swipeLastX = e.touches[0].clientX;
    swipeLastTime = now;

    // Apply rubber-band resistance at edges
    let effectiveDrag = dx;
    const isAtStart = currentAlertIndex === 0 && dx > 0;
    const isAtEnd = currentAlertIndex === allDisplayedAlerts.length - 1 && dx < 0;
    if (isAtStart || isAtEnd) {
        effectiveDrag = dx * 0.25; // Rubber band
    }

    const baseOffset = getCarouselOffset(currentAlertIndex);
    setCarouselTransform(baseOffset + effectiveDrag, false);
}

function handleRowSwipeEnd(e) {
    if (window.innerWidth > 768) return;
    if (isListMode) return;

    if (!isSwiping) return;
    isSwiping = false;

    const dx = e.changedTouches[0].clientX - swipeStartX;

    // Decide: snap to next, previous, or stay
    const slotWidth = getCardSlotWidth();
    const threshold = slotWidth * 0.2; // 20% of card width
    const velocityThreshold = 0.3; // px/ms

    let targetIndex = currentAlertIndex;

    if (dx < -threshold || swipeVelocity < -velocityThreshold) {
        // Swiped left → next
        targetIndex = Math.min(currentAlertIndex + 1, allDisplayedAlerts.length - 1);
    } else if (dx > threshold || swipeVelocity > velocityThreshold) {
        // Swiped right → prev
        targetIndex = Math.max(currentAlertIndex - 1, 0);
    }

    if (targetIndex !== currentAlertIndex) {
        focusAlertByIndex(targetIndex);
    } else {
        // Snap back to current position
        snapCarousel();
    }
}


// =============================================================================
// Data Filtering
// =============================================================================

/**
 * Get alerts sorted by distance, filtered to within the user's radius.
 * When no user location is set, returns all located alerts (newest first).
 */
export function getFilteredAlerts() {
    let located = Array.from(alerts.values())
        .filter(a => a.latitude && a.longitude)
        .map(a => {
            a._distance = getCachedDistance(a);
            return a;
        });

    if (userLocation) {
        located = located.filter(a => a._distance !== undefined && a._distance <= filterRadiusKm);
        located.sort((a, b) => (a._distance ?? 999) - (b._distance ?? 999));
    } else {
        // No location: show all, newest first
        located.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }

    return located;
}


// =============================================================================
// Rendering
// =============================================================================

/**
 * Format time as "X min geleden" or "X uur geleden"
 */
function formatTimeAgo(timestamp) {
    return formatAge(timestamp).text;
}

/**
 * Build compact tags HTML for a panel row (max 3 tags).
 */
function buildRowTagsHtml(alert) {
    const tags = alert.tags;
    if (!tags || tags.length === 0) return '';

    const tagsHtml = tags.slice(0, 3).map(tag => {
        return `<span class="tag ${tag.class || ''}">${tag.label}</span>`;
    }).join('');

    return `<div class="row-tags">${tagsHtml}</div>`;
}


/**
 * Build one alert row. `index` is the alert's position in `allDisplayedAlerts`
 * (its navigation index); `total` is the alert count for the "X / Y" position.
 */
function renderAlertRow(alert, index, total) {
    const hasLocation = alert.latitude && alert.longitude;

    // Single distance per card: prefer the driving distance (fetched for the
    // focused alert) and fall back to the straight-line "hemelsbreed" figure.
    // Never both, and no driving time — keeps the header uncluttered.
    let distanceHtml = '';
    if (alert._distance !== undefined) {
        const routeData = getCachedRouteDistance(alert.id);
        const km = routeData ? routeData.distance_km : alert._distance;
        const kmText = km < 10 ? km.toFixed(1) : Math.round(km).toString();
        const modeHtml = routeData ? '<span class="distance-mode">🚗</span>' : '';
        const title = routeData ? 'Rijafstand' : 'Hemelsbreed';

        distanceHtml = `
            <div class="row-distance" title="${title}">
                ${modeHtml}<span class="distance-num">${kmText}</span><span class="distance-unit">km</span>
            </div>
        `;
    }

    const timeAgo = formatTimeAgo(alert.timestamp);
    // Cause-led primary is the short "what?" word ("Gaslucht",
    // "Spoedinzet brandweer"). Location is rendered below as a separate
    // line so the narrow mobile card never truncates the title.
    const display = alert.display;
    const primary = display?.primary || alert.primary_title || '';
    const locationShort = display?.location_short || alert.address || '';
    // prose_title already contains the location, so it replaces the separate
    // address line. Falls back to the short cause for payloads cached before
    // the field existed.
    const lede = display?.prose_title || primary;
    const responseLabel = display?.response_label || '';
    const zwMarker = isZwaailicht(alert)
        ? '<span class="zwaailicht-marker" title="Met zwaailicht — spoed (A1/P1)" aria-label="met zwaailicht"></span>'
        : '';
    const ledeHtml = lede ? escapeHtml(lede) + zwMarker : '';
    const detailUrl = getAlertDetailUrl(alert);
    // Single share affordance, right-aligned in the meta row. The detail
    // page is reached by the title link (and tapping the active card).
    const shareBtnHtml = `<button class="row-share-btn" onclick="handleShareClick('${alert.id}')" title="Deel melding" aria-label="Deel melding"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></button>`;
    const tagsHtml = buildRowTagsHtml(alert);
    const trending = getAlertTrending(alert.id);
    let trendingHtml = '';
    if (trending.trending) {
        trendingHtml = `<span class="tag tag-trending">populair</span>`;
    }
    // Escape on every render. The previous `alert._escapedDesc ??= …`
    // memoisation looked harmless but would silently swallow updated
    // descriptions if the WebSocket protocol ever grows an in-place
    // patch event. escapeHtml on a ≤280-char string is essentially free.
    const rawMessage = alert.message ? escapeHtml(alert.message) : '';

    // Same three registers as the server-rendered row (_alert_row.html):
    // the incident (cause-led prose, carries the location), the response
    // (service + urgency, units, time) and the untouched signal. The old card
    // led with a short cause word plus an address line, then repeated both in
    // the generated description.
    const respParts = [];
    if (responseLabel && !lede.startsWith(responseLabel)) respParts.push(responseLabel);
    // (no city chip here: prose_title already ends with the town, and the SPA
    // panel has no cross-city list where the town would add information)
    for (const chip of (display?.metadata_chips || [])) {
        if (chip?.label) respParts.push(chip.label);
    }
    const responseHtml = respParts.length
        ? `<span class="row-response">${respParts.map(escapeHtml).join(' · ')}</span>`
        : '';

    const descriptionHtml = rawMessage ? `<div class="row-message">${rawMessage}</div>` : '';

    const positionHtml = `<span class="row-position" aria-hidden="true">${index + 1} / ${total}</span>`;

    return `
        <div class="alert-row ${alert.service} ${!hasLocation ? 'unlocated' : ''}"
             data-index="${index}" data-id="${alert.id}" role="listitem">
            <div class="row-top">
                <div class="row-icon ${alert.service}"></div>
                <div class="row-body">
                    ${ledeHtml ? `<div class="row-lede"><a class="row-primary-link" href="${detailUrl}">${ledeHtml}</a></div>` : ''}
                    ${locationShort ? '' : '<div class="row-address unknown">Locatie onbekend</div>'}
                    <div class="row-details">
                        ${responseHtml}
                        <span class="row-time">${timeAgo}</span>
                        ${trendingHtml}
                        ${tagsHtml}
                        ${shareBtnHtml}
                    </div>
                </div>
                ${distanceHtml}
            </div>
            ${descriptionHtml}
            ${positionHtml}
        </div>
    `;
}


/**
 * Render the alert panel list
 */
export function renderCarousel() {
    if (!listEl) return;

    const located = getFilteredAlerts();
    const displayedAlerts = [...located];

    // Save current alert ID BEFORE overwriting the displayed list
    const previousAlertId = allDisplayedAlerts[currentAlertIndex]?.id;
    setAllDisplayedAlerts(displayedAlerts);

    if (displayedAlerts.length === 0) {
        const hasLocation = !!userLocation;
        listEl.innerHTML = `
            <div class="panel-empty">
                <p>${hasLocation
                    ? 'Geen meldingen in de buurt'
                    : 'Klik op de kaart om je locatie in te stellen'
                }</p>
                <p class="empty-hint">${hasLocation
                    ? 'Alles is rustig'
                    : 'Dan zie je meldingen bij jou in de buurt'
                }</p>
            </div>
        `;
        updateViewToggleEnabled();
        return;
    }

    // Weave burst cards into the alert list by the same ordering the alerts
    // use — proximity when a location is set, recency otherwise — instead of
    // pinning every burst to the top. A far-away story burst now sinks to
    // where its distance puts it rather than burying the nearby feed.
    const items = [
        ...displayedAlerts.map((alert, index) => ({
            kind: 'alert',
            alert,
            index,
            sortKey: userLocation
                ? (alert._distance ?? Infinity)
                : -new Date(alert.timestamp).getTime(),
        })),
        ...getPanelBursts().map((burst) => ({
            kind: 'burst',
            burst,
            sortKey: userLocation
                ? (getBurstDistance(burst) ?? Infinity)
                : -new Date(burst.last_alert_at || burst.started_at).getTime(),
        })),
    ];
    items.sort((a, b) => a.sortKey - b.sortKey);

    // Remember each alert's DOM slot so the mobile carousel centres correctly
    // now that burst cards can sit between alerts (see getCarouselOffset).
    _alertSlotByIndex = [];
    items.forEach((item, slot) => {
        if (item.kind === 'alert') _alertSlotByIndex[item.index] = slot;
    });

    listEl.innerHTML = items.map((item) =>
        item.kind === 'burst'
            ? renderBurstCard(item.burst)
            : renderAlertRow(item.alert, item.index, displayedAlerts.length)
    ).join('');

    // DOM was rebuilt — reset tracking so updateActiveRow starts clean
    _prevActiveIndex = -1;

    // Restore focus to previously active alert
    let newIndex = 0;
    if (previousAlertId) {
        const found = displayedAlerts.findIndex(a => a.id === previousAlertId);
        if (found >= 0) {
            newIndex = found;
        } else if (displayedAlerts[0]) {
            focusMapOnAlert(displayedAlerts[0]);
        }
    } else if (displayedAlerts[0]) {
        focusMapOnAlert(displayedAlerts[0]);
    }
    setCurrentAlertIndex(newIndex);
    updateActiveRow(newIndex);

    // Position carousel on mobile
    const isMobile = window.innerWidth <= 768;
    if (isMobile && !isListMode) {
        // Set position immediately (no animation on re-render)
        setCarouselTransform(getCarouselOffset(newIndex), false);
        // Double-rAF: ensure position is painted before re-enabling transitions
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                listEl?.classList.remove('dragging');
            });
        });
    }

    updateViewToggleEnabled();
}


/**
 * Update active row highlighting — only touches the 2 rows that changed.
 */
let _prevActiveIndex = -1;

function updateActiveRow(index) {
    if (!listEl) return;

    // Remove active from previous row
    if (_prevActiveIndex >= 0 && _prevActiveIndex !== index) {
        const prevRow = listEl.querySelector(`.alert-row[data-index="${_prevActiveIndex}"]`);
        if (prevRow) prevRow.classList.remove('active');
    }

    // Add active to new row
    const newRow = listEl.querySelector(`.alert-row[data-index="${index}"]`);
    if (newRow) newRow.classList.add('active');

    _prevActiveIndex = index;
}


/**
 * Disable the view-toggle when there's nothing to expand into a list. Always
 * leave it enabled in list mode so the user can collapse back to the map even
 * if the feed momentarily empties (the position indicator itself lives inside
 * each card and is shown via CSS on .active).
 */
function updateViewToggleEnabled() {
    if (!viewToggleBtn) return;
    const total = allDisplayedAlerts.length;
    viewToggleBtn.disabled = total === 0 && !isListMode;
}


/**
 * Focus map on an alert (pan, highlight marker, show popup)
 */
function focusMapOnAlert(alert) {
    if (!alert?.latitude || !alert?.longitude) return;

    updateTrackingBeam(alert);
    highlightMarker(alert.id);
    openMarkerPopup(alert.id);

    if (userLocation) {
        fitBoundsToAlert(alert);
    } else {
        setView(alert.latitude, alert.longitude);
    }
}


// =============================================================================
// Burst Cards
// =============================================================================

/**
 * The bursts that earn a panel card: every live burst (spiking now), plus
 * recently-ended "story" bursts that linked real news — surfacing the enriched
 * summaries that otherwise only live on the detail page. Ended bursts without
 * news stay off the panel. The map set they come from is already bounded to the
 * recent coherent window, so no separate cap is needed. renderCarousel() weaves
 * these into the alert list by proximity rather than pinning them to the top.
 */
function getPanelBursts() {
    const result = [];
    for (const burst of activeBursts.values()) {
        if (burst.is_active === false && (burst.news_article_count || 0) === 0) continue;
        result.push(burst);
    }
    return result;
}

/** Straight-line distance (km) from the user to a burst's centroid, or
 *  undefined when there's no location or centroid — used to weave burst cards
 *  into the proximity-sorted alert list. */
function getBurstDistance(burst) {
    if (!userLocation || burst.centroid_lat == null || burst.centroid_lon == null) {
        return undefined;
    }
    return getDistanceKm(userLocation.lat, userLocation.lon, burst.centroid_lat, burst.centroid_lon);
}


/** Build one burst card. Live bursts pulse; ended stories read as settled. */
function renderBurstCard(burst) {
    const isLive = burst.is_active !== false;
    const label = escapeHtml(burst.scope_label || burst.scope || '');
    const count = burst.current_count || burst.peak_count || 0;
    const url = getBurstUrl(burst);
    const title = burst.title ? escapeHtml(burst.title) : '';
    const summary = burst.summary ? escapeHtml(burst.summary) : '';
    const newsCount = burst.news_article_count || 0;

    // Straight-line distance to the burst centroid, mirroring the alert row's
    // "hemelsbreed" figure — so a card interleaved by proximity shows why it
    // sits where it does. Reuses .row-distance for identical treatment.
    const distance = getBurstDistance(burst);
    let distanceHtml = '';
    if (distance !== undefined) {
        const kmText = distance < 10 ? distance.toFixed(1) : Math.round(distance).toString();
        distanceHtml = `<div class="row-distance" title="Hemelsbreed"><span class="distance-num">${kmText}</span><span class="distance-unit">km</span></div>`;
    }

    // Header marker distinguishes state: live bursts get the pulsing Live pill;
    // ended stories get a static "in het nieuws" pill (they only reach the
    // panel when news is linked). Stats mirror that — a live window vs an age.
    let marker, newsChip, stats;
    if (isLive) {
        const minutes = burst.window_minutes || 15;
        marker = '<span class="burst-card-live"><span class="burst-card-pulse-dot"></span>Live</span>';
        newsChip = newsCount > 0 ? `<span class="burst-card-news">📰 ${newsCount}</span>` : '';
        stats = `<span>${count} meldingen in ${minutes} min</span>`;
    } else {
        const age = burst.ended_at ? formatAge(burst.ended_at).text : '';
        const ageLabel = age === 'NU' ? 'zojuist' : (age ? `${age} geleden` : '');
        // Up-arrow marks a settled burst pulled back to the top because news
        // linked to it — the "opnieuw actueel" signal. Feather line icon, so
        // it inherits the pill colour via currentColor.
        marker = '<span class="burst-card-updated">'
            + '<svg class="burst-card-updated-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" '
            + 'stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
            + '<line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>'
            + 'in het nieuws</span>';
        newsChip = '';
        stats = `<span>${count} meldingen</span>${ageLabel ? `<span>${ageLabel}</span>` : ''}`;
    }

    return `
        <a href="${encodeURI(url)}" class="burst-panel-card">
            <div class="burst-card-header">
                ${marker}
                <span class="burst-card-label">${label}</span>
                ${newsChip}
                ${distanceHtml}
            </div>
            ${title ? `<div class="burst-card-title">${title}</div>` : ''}
            ${summary ? `<p class="burst-card-summary">${summary}</p>` : ''}
            <div class="burst-card-stats">${stats}</div>
        </a>
    `;
}


// =============================================================================
// Navigation
// =============================================================================

/**
 * Focus on specific alert by ID
 */
export function focusAlert(id) {
    const index = allDisplayedAlerts.findIndex(a => a.id === id);
    if (index >= 0) {
        focusAlertByIndex(index);
    }
}


/**
 * Focus on specific alert by index (keyboard nav / marker click)
 * Pans map and updates route
 */
export function focusAlertByIndex(index) {
    if (index < 0 || index >= allDisplayedAlerts.length) return;

    setCurrentAlertIndex(index);
    updateActiveRow(index);

    const isMobile = window.innerWidth <= 768;

    if (isMobile && !isListMode) {
        // Snap carousel to the new index
        snapCarousel();
    }

    // Scroll row into view (desktop always, mobile only in list mode)
    const rows = listEl?.querySelectorAll('.alert-row');
    if (rows?.[index]) {
        if (!isMobile || isListMode) {
            rows[index].scrollIntoView({ behavior: 'smooth', block: isMobile ? 'center' : 'nearest' });
        }
    }

    updateViewToggleEnabled();

    // Focus map
    const alert = allDisplayedAlerts[index];
    if (alert) {
        focusMapOnAlert(alert);
    }

    // Fetch route distance for this alert
    if (typeof window.onAlertFocusChanged === 'function') {
        window.onAlertFocusChanged();
    }

}

/**
 * Scroll list to top
 */
export function scrollToStart() {
    if (listEl) {
        listEl.scrollTo({ top: 0, behavior: 'smooth' });
    }
}

/**
 * Close mobile overlays (list mode) — called on map tap
 */
export function closeMobileOverlays() {
    if (window.innerWidth > 768) return;
    if (isListMode) toggleListMode();
}


/**
 * Handle share button click - exposed globally for onclick handlers
 */
window.handleShareClick = async function(alertId) {
    const alert = alerts.get(alertId);
    if (!alert) return;

    try {
        await shareAlert(alert);
    } catch (err) {
        console.warn('Share failed:', err);
    }
};
